import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTreeModule, MatTreeNestedDataSource } from '@angular/material/tree';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import { Patient } from '@principle-theorem/principle-core';
import { type IPatient } from '@principle-theorem/principle-core/interfaces';
import {
  DocumentArchive,
  Firestore,
  serialise,
  shareReplayCold,
  type ArchivedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { JsonEditorOptions, NgJsonEditorModule } from 'ang-jsoneditor';
import { from, type Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export interface IPatientDetailsHistoryDialogData {
  patient: WithRef<IPatient>;
}

interface IHistoryItem {
  document: ArchivedDocument<IPatient>;
  serialisedDocument: object;
  label: string;
  name: string;
}

function toHistoryItem(doc: ArchivedDocument<IPatient>): IHistoryItem {
  return {
    document: doc,
    serialisedDocument: serialise(doc),
    label: DocumentArchive.getLabel(doc),
    name: doc.name,
  };
}

@Component({
  selector: 'pt-patient-details-history-dialog',
  templateUrl: './patient-details-history-dialog.component.html',
  styleUrls: ['./patient-details-history-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    MatTreeModule,
    MatProgressSpinnerModule,
    NgJsonEditorModule,
  ],
})
export class PatientDetailsHistoryDialogComponent {
  data = inject<IPatientDetailsHistoryDialogData>(MAT_DIALOG_DATA);
  editorOptions: JsonEditorOptions;
  expandedNodes = signal(new Set<IHistoryItem>());

  dataSource = new MatTreeNestedDataSource<IHistoryItem>();
  historyItems$: Observable<IHistoryItem[]>;
  loading$: Observable<boolean>;

  constructor() {
    this.editorOptions = new JsonEditorOptions();
    this.editorOptions.mode = 'view';
    this.editorOptions.mainMenuBar = false;
    this.editorOptions.navigationBar = true;
    this.editorOptions.statusBar = false;

    this.historyItems$ = from(
      Firestore.getDocs(Patient.archiveCol(this.data.patient))
    ).pipe(
      map((docs) =>
        docs
          .sort((a, b) => DocumentArchive.sortByArchivedAt(a, b))
          .map(toHistoryItem)
      ),
      shareReplayCold()
    );

    this.loading$ = this.historyItems$.pipe(
      map(() => false),
      startWith(true),
      shareReplayCold()
    );

    this.historyItems$.pipe(takeUntilDestroyed()).subscribe((items) => {
      this.dataSource.data = items;
    });
  }

  childrenAccessor(): IHistoryItem[] {
    return [];
  }

  toggleNode(node: IHistoryItem): void {
    this.expandedNodes.update((nodes) => {
      const next = new Set(nodes);
      if (next.has(node)) {
        next.delete(node);
      } else {
        next.add(node);
      }
      return next;
    });
  }

  isExpanded(node: IHistoryItem): boolean {
    return this.expandedNodes().has(node);
  }
}
